import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { UserService } from '../services/user.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.css'
})
export class ResetPasswordComponent implements OnInit {
  token: string = '';
  newPassword: string = '';
  confirmPassword: string = '';
  loading: boolean = false;
  validatingToken: boolean = true;
  success: boolean = false;
  error: string | null = null;
  tokenInvalid: boolean = false;
  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserService
  ) {}

  ngOnInit(): void {
    // Get token from query params
    this.route.queryParams.subscribe(params => {
      this.token = params['token'] || '';
      
      if (!this.token) {
        this.error = 'Invalid reset link. Please request a new password reset.';
        this.validatingToken = false;
      } else {
        // Validate token immediately on page load
        this.validateToken();
      }
    });
  }

  validateToken(): void {
    this.validatingToken = true;
    this.error = null;
    this.tokenInvalid = false;

    this.userService.validateResetToken(this.token).subscribe({
      next: (response) => {
        this.validatingToken = false;
        if (!response.valid) {
          this.error = response.message || 'This reset link is invalid, expired, or has already been used. Please request a new password reset.';
          this.tokenInvalid = true;
        }
      },
      error: (err) => {
        this.validatingToken = false;
        console.error('Error validating token:', err);
        
        if (err.status === 400 || err.status === 404) {
          this.error = 'This reset link is invalid, expired, or has already been used. Please request a new password reset.';
          this.tokenInvalid = true;
        } else {
          this.error = 'Unable to verify reset link. Please try again or request a new password reset.';
          this.tokenInvalid = true;
        }
      }
    });
  }

  onSubmit(): void {
    this.error = null;

    // Validation
    if (!this.newPassword || !this.confirmPassword) {
      this.error = 'Please fill in all fields';
      return;
    }

    if (this.newPassword !== this.confirmPassword) {
      this.error = 'Passwords do not match';
      return;
    }

    if (this.newPassword.length < 8) {
      this.error = 'Password must be at least 8 characters';
      return;
    }

    this.loading = true;

    this.userService.resetPasswordWithToken(this.token, this.newPassword).subscribe({
      next: (response) => {
        this.loading = false;
        this.success = true;
      },
      error: (err) => {
        this.loading = false;
        console.error('Error resetting password:', err);
        
        if (err.status === 400) {
          this.error = 'This reset link is invalid, expired, or has already been used. Please request a new password reset.';
          this.tokenInvalid = true;
        } else {
          this.error = 'An error occurred. Please try again.';
        }
      }
    });
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  backToLogin(): void {
    this.router.navigate(['/login']);
  }
}
